import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { myTeamFirst, scoreboardWeek } from '@ff/core';
import type { SeasonContext } from '../lib/seasonContext.ts';
import { useMe } from '../lib/me.tsx';
import {
  cancelTipSheet,
  fetchTipSheet,
  fetchTipSheetWeeks,
  generateTipSheet,
  publishTipSheet,
  regenerateTipSheetRankings,
  regenerateTipSheetTeam,
  type TipSheet,
  type TipSheetSummary,
} from '../lib/api.ts';
import { relativeTime } from '../lib/format.ts';
import { ManagerLink } from '../components/ManagerLink.tsx';
import { TipSheetTeamCard } from '../components/TipSheetTeam.tsx';

/**
 * Without word from the server, a sheet that has said "generating" this long
 * is assumed to belong to a process that died. The server's `active` flag
 * overrides the guess in both directions.
 */
const STALE_AFTER_MS = 10 * 60 * 1000;
const POLL_MS = 5_000;
/** A dead-looking sheet still gets checked, just not every five seconds. */
const STALLED_POLL_MS = 30_000;

/**
 * The weekly Tip Sheet: power rankings, then every team's lineup advice with
 * the reader's own team first. Everyone reads it; the commissioner generates
 * it, reads the draft, and publishes it.
 */
export function TipSheetPage() {
  const { stored, nameOf, teamOf } = useOutletContext<SeasonContext>();
  const me = useMe();
  const data = stored.bundle.data;
  const season = data.season;
  const isCommish = me?.isCommissioner === true;

  const [weeks, setWeeks] = useState<TipSheetSummary[] | null>(null);
  const [params] = useSearchParams();
  const linkedWeek = Number(params.get('week'));
  const [week, setWeek] = useState<number | null>(null);
  const [sheet, setSheet] = useState<TipSheet | null | undefined>(undefined);
  // Which fetch is the latest. Switching weeks while a slow request is in
  // flight must not let the old week's sheet land under the new week's picker,
  // with Publish and Regenerate buttons that would act on the wrong week.
  const requestId = useRef(0);
  const weeksRequestId = useRef(0);
  // The week on screen right now, for checking a mutation's answer against
  // the selection at the moment it lands rather than when it was sent.
  const currentWeek = useRef<number | null>(null);
  useEffect(() => {
    currentWeek.current = week;
  }, [week]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The weeks on offer: every sheet the caller may see, plus, for the
  // commissioner, the week ESPN is pointed at so there is something to generate.
  const options = useMemo(() => {
    const set = new Set((weeks ?? []).map((w) => w.week));
    if (isCommish) {
      for (const w of [data.currentWeek, scoreboardWeek(data)]) {
        if (w != null && data.matchups.some((m) => m.week === w)) set.add(w);
      }
    }
    return [...set].sort((a, b) => b - a);
  }, [weeks, isCommish, data]);

  const loadWeeks = useCallback(async () => {
    const id = ++weeksRequestId.current;
    try {
      const list = await fetchTipSheetWeeks(season);
      // A slow answer for the season we just left must not fill this
      // season's picker with the wrong weeks.
      if (id === weeksRequestId.current) setWeeks(list);
    } catch (e) {
      if (id !== weeksRequestId.current) return;
      setError(e instanceof Error ? e.message : 'Could not load');
      setWeeks([]);
    }
  }, [season]);

  useEffect(() => {
    setWeeks(null);
    setWeek(null);
  }, [season]);

  useEffect(() => {
    void loadWeeks();
  }, [loadWeeks]);

  useEffect(() => {
    if (options.length === 0) return;
    setWeek((w) => {
      if (options.includes(linkedWeek)) return linkedWeek;
      return w != null && options.includes(w) ? w : options[0] ?? null;
    });
  }, [options, linkedWeek]);

  const load = useCallback(async () => {
    if (week == null) return;
    const id = ++requestId.current;
    try {
      const next = await fetchTipSheet(season, week);
      if (id !== requestId.current) return;
      // A generation we just started answers with its document, but a poll
      // can still land before Firestore has it. Keep what we know rather than
      // flipping to "no sheet" and switching the polling off.
      setSheet((cur) =>
        next === null && cur?.status === 'generating' && !isStalled(cur) ? cur : next,
      );
    } catch (e) {
      if (id !== requestId.current) return;
      setError(e instanceof Error ? e.message : 'Could not load');
      setSheet(null);
    }
  }, [season, week]);

  useEffect(() => {
    setSheet(undefined);
    setError(null);
    void load();
  }, [load]);

  const generating = sheet?.status === 'generating';
  const stalled = generating && isStalled(sheet);
  // A single team or the rankings being rewritten also wants the poll.
  const rewriting =
    !!sheet && (sheet.teams.some((t) => t.regeneratingAt) || !!sheet.rankingsRegeneratingAt);

  // While it says it's writing, keep asking; the document carries its own
  // progress. A run the server has disowned is still checked, more slowly,
  // because a regenerate on top of a run that just finished is a paid mistake.
  useEffect(() => {
    if (!generating && !rewriting) return;
    const t = setInterval(() => void load(), stalled ? STALLED_POLL_MS : POLL_MS);
    return () => clearInterval(t);
  }, [generating, rewriting, stalled, load]);

  async function handleGenerate() {
    if (week == null) return;
    if (
      sheet?.status === 'published' &&
      !window.confirm(`This unpublishes the Week ${week} sheet until you publish it again. Continue?`)
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // The 202 carries the `generating` document; showing it starts the poll.
      const started = await generateTipSheet(season, week);
      if (started.week === currentWeek.current && started.season === season) {
        requestId.current += 1;
        setSheet(started);
      }
      await loadWeeks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not generate');
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (week == null) return;
    setBusy(true);
    setError(null);
    try {
      const published = await publishTipSheet(season, week);
      if (published.week === currentWeek.current && published.season === season) {
        requestId.current += 1;
        setSheet(published);
      }
      await loadWeeks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not publish');
    } finally {
      setBusy(false);
    }
  }

  // Stops the run, a team rewrite or a rankings rewrite, whichever is going.
  // The answer is the sheet as the server left it, so the poll settles at once.
  async function handleCancel() {
    if (week == null) return;
    setBusy(true);
    setError(null);
    try {
      const next = await cancelTipSheet(season, week);
      if (next.week === currentWeek.current && next.season === season) {
        requestId.current += 1;
        setSheet(next);
      }
      await loadWeeks();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel');
    } finally {
      setBusy(false);
    }
  }

  // One team, or the rankings, rewritten in place. The answer is the sheet
  // with the stamp on it; the poll brings the result.
  async function handleRewrite(run: () => Promise<TipSheet>) {
    if (week == null) return;
    setBusy(true);
    setError(null);
    try {
      const next = await run();
      if (next.week === currentWeek.current && next.season === season) {
        requestId.current += 1;
        setSheet(next);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not regenerate');
    } finally {
      setBusy(false);
    }
  }

  const ordered = useMemo(
    () => (sheet ? myTeamFirst(sheet.teams, data.owners, me?.name) : []),
    [sheet, data.owners, me?.name],
  );
  const mineId = matchedOwner(data.owners, me?.name);

  if (weeks === null) {
    return (
      <div className="space-y-4">
        <Header season={season} />
        <p className="animate-pulse font-mono text-sm text-chalk-dim">loading…</p>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="space-y-4">
        <Header season={season} />
        <p className="text-sm text-chalk-dim">No tip sheet yet. One goes up each week before kickoff.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Header season={season} />

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="eyebrow">Week</span>
          <select
            value={week ?? ''}
            onChange={(e) => setWeek(Number(e.target.value))}
            className="rounded-md border border-felt-line bg-felt-raised px-2 py-1 font-mono text-sm text-chalk"
          >
            {options.map((w) => (
              <option key={w} value={w}>
                {w}
                {weeks.some((s) => s.week === w) ? '' : ' (none yet)'}
              </option>
            ))}
          </select>
        </label>

        {sheet && <StatusPill sheet={sheet} stalled={stalled} />}

        {isCommish && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerate}
              disabled={busy || (generating && !stalled)}
              className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {generating && !stalled ? 'Writing…' : sheet ? 'Regenerate' : 'Generate'}
            </button>
            {((generating && !stalled) || rewriting) && (
              <button
                onClick={handleCancel}
                disabled={busy}
                className="rounded-md border border-blood/50 px-3 py-1.5 font-display text-sm font-bold text-blood transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            {sheet?.status === 'draft' && !sheet.error && (
              <button
                onClick={handlePublish}
                disabled={busy}
                className="rounded-md bg-chalk px-3 py-1.5 font-display text-sm font-bold text-felt transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                Publish
              </button>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-blood">{error}</p>}

      {sheet === undefined ? (
        <p className="animate-pulse font-mono text-sm text-chalk-dim">loading…</p>
      ) : sheet === null ? (
        <p className="text-sm text-chalk-dim">
          No sheet for Week {week} yet.
          {isCommish ? ' Hit Generate to write one.' : ''}
        </p>
      ) : (
        <>
          {sheet.error && (
            <div className="card p-6">
              <p className="font-display text-lg font-bold">This one didn't get written.</p>
              <p className="mt-1 text-sm text-chalk-dim">{sheet.error}</p>
            </div>
          )}

          {sheet.status === 'draft' && isCommish && !sheet.error && (
            <p className="font-mono text-xs text-chalk-dim">
              Draft. Only you can see this until you publish it.
            </p>
          )}

          {(sheet.powerRankings.length > 0 || sheet.rankingsRegeneratingAt || sheet.rankingsError) && (
            <section className="card p-6">
              <div className="flex flex-wrap items-center gap-3">
                <p className="eyebrow">Power rankings</p>
                {sheet.rankingsRegeneratingAt && <RewritingPill />}
                {isCommish && !generating && (
                  <button
                    onClick={() =>
                      void handleRewrite(() => regenerateTipSheetRankings(season, week as number))
                    }
                    disabled={busy || !!sheet.rankingsRegeneratingAt}
                    className="ml-auto rounded-md border border-felt-line px-2 py-1 font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim hover:text-brass disabled:opacity-50"
                  >
                    Regenerate rankings
                  </button>
                )}
              </div>
              <ol className="mt-3 space-y-3">
                {sheet.powerRankings.map((r) => (
                  <li key={r.ownerId} className="flex gap-3">
                    <span className="w-6 shrink-0 font-mono text-lg tabular text-chalk-dim">{r.rank}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <ManagerLink ownerId={r.ownerId} className="font-display font-bold">
                          {nameOf(r.ownerId)}
                        </ManagerLink>
                        <span className="truncate font-mono text-[0.65rem] uppercase tracking-wide text-chalk-dim">
                          {teamOf(r.ownerId)}
                        </span>
                        <Movement rank={r.rank} previous={r.previousRank} />
                      </div>
                      <p className="font-editorial text-sm leading-snug text-chalk">{r.blurb}</p>
                    </div>
                  </li>
                ))}
              </ol>
              {sheet.rankingsError && (
                <p className="mt-3 font-mono text-xs text-blood">
                  Power rankings didn't come through: {sheet.rankingsError}
                </p>
              )}
            </section>
          )}

          {sheet.teams.length > 0 && !mineId && (
            <p className="font-mono text-xs text-chalk-dim">
              Couldn't match you to a team; tap yours below.
            </p>
          )}

          {ordered.map((team) => (
            <TeamSection
              key={team.ownerId}
              team={team}
              name={nameOf(team.ownerId)}
              teamName={teamOf(team.ownerId)}
              open={team.ownerId === mineId}
              nameOf={nameOf}
              sheetGeneratedAt={sheet.generatedAt}
              onRegenerate={
                isCommish && !generating
                  ? () =>
                      void handleRewrite(() =>
                        regenerateTipSheetTeam(season, week as number, team.ownerId),
                      )
                  : undefined
              }
              busy={busy}
            />
          ))}

          {generating && sheet.progress && (
            <p className="font-mono text-xs text-chalk-dim">
              {sheet.progress.done} of {sheet.progress.total} teams written…
            </p>
          )}

          {sheet.lineupsFetchedAt && (
            <p className="font-mono text-[0.7rem] text-chalk-dim">
              Rosters as of {asOf(sheet.lineupsFetchedAt)}
              {sheet.generatedAt ? ` · written ${new Date(sheet.generatedAt).toLocaleDateString()}` : ''}
              {' · '}
              {sheet.model}
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Generating, but nobody is actually generating it. The server knows; failing that, the clock guesses. */
function isStalled(sheet: TipSheet | null | undefined): boolean {
  if (!sheet || sheet.status !== 'generating') return false;
  if (typeof sheet.active === 'boolean') return !sheet.active;
  return !!sheet.startedAt && Date.now() - Date.parse(sheet.startedAt) > STALE_AFTER_MS;
}

/** "3h ago" while it's recent, a plain date once it isn't. */
function asOf(iso: string): string {
  const rel = relativeTime(iso);
  return /^(just now|\d+[mhd])$/.test(rel) ? (rel === 'just now' ? rel : `${rel} ago`) : rel;
}

function matchedOwner(owners: { id: string; name: string }[], viewerName: string | null | undefined): string | null {
  if (!viewerName) return null;
  const wanted = viewerName.trim().toLowerCase();
  return owners.find((o) => o.name.trim().toLowerCase() === wanted)?.id ?? null;
}

function TeamSection({
  team,
  name,
  teamName,
  open,
  nameOf,
  sheetGeneratedAt,
  onRegenerate,
  busy,
}: {
  team: TipSheet['teams'][number];
  name: string;
  teamName: string;
  open: boolean;
  nameOf: (id: string) => string;
  sheetGeneratedAt?: string;
  /** Present for the commissioner; rewrites just this team. */
  onRegenerate?: () => void;
  busy: boolean;
}) {
  const [expanded, setExpanded] = useState(open);
  useEffect(() => setExpanded(open), [open, team.ownerId]);
  const rewriting = !!team.regeneratingAt;
  const counts = [
    team.swaps.length > 0 && `${team.swaps.length} swap${team.swaps.length === 1 ? '' : 's'}`,
    team.waivers.length > 0 && `${team.waivers.length} waiver${team.waivers.length === 1 ? '' : 's'}`,
    team.error && 'failed',
  ].filter(Boolean);
  // "Rewritten" only when this team was redone after the sheet itself.
  const rewrittenAt =
    team.generatedAt && (!sheetGeneratedAt || team.generatedAt > sheetGeneratedAt)
      ? team.generatedAt
      : null;

  return (
    <section className="card">
      <div className="flex items-baseline gap-3 px-6 py-4">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex min-w-0 flex-1 items-baseline justify-between gap-3 text-left"
        >
          <span className="min-w-0">
            <span className="font-display text-lg font-bold">{name}</span>
            <span className="ml-2 font-mono text-[0.65rem] uppercase tracking-wide text-chalk-dim">{teamName}</span>
            {open && <span className="ml-2 font-mono text-[0.6rem] uppercase tracking-wider text-brass">you</span>}
            {rewriting && (
              <span className="ml-2 inline-block align-middle">
                <RewritingPill />
              </span>
            )}
          </span>
          <span className="shrink-0 font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
            {counts.length > 0 ? `${counts.join(' · ')} ` : ''}
            {expanded ? '▾' : '▸'}
          </span>
        </button>
        {onRegenerate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRegenerate();
            }}
            disabled={busy || rewriting}
            className="shrink-0 rounded-md border border-felt-line px-2 py-1 font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim hover:text-brass disabled:opacity-50"
            aria-label={`Regenerate ${name}'s sheet`}
          >
            {rewriting ? 'Rewriting…' : 'Regenerate'}
          </button>
        )}
      </div>
      {expanded && (
        <div className="border-t border-felt-line px-6 pb-6 pt-4">
          <TipSheetTeamCard team={team} nameOf={nameOf} />
          {rewrittenAt && (
            <p className="mt-4 font-mono text-[0.65rem] text-chalk-dim">
              Rewritten {asOf(rewrittenAt)}.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function RewritingPill() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-brass/15 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-brass">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brass" />
      Rewriting
    </span>
  );
}

function StatusPill({ sheet, stalled }: { sheet: TipSheet; stalled: boolean }) {
  if (sheet.status === 'generating') {
    return stalled ? (
      <span className="rounded-full border border-blood/40 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-blood">
        Stalled
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-brass/15 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-brass">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brass" />
        Writing{sheet.progress ? ` ${sheet.progress.done}/${sheet.progress.total}` : ''}
      </span>
    );
  }
  if (sheet.status === 'published') {
    return (
      <span className="rounded-full bg-cash/15 px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-cash">
        Published
      </span>
    );
  }
  return (
    <span className="rounded-full border border-felt-line px-2 py-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-chalk-dim">
      Draft
    </span>
  );
}

function Movement({ rank, previous }: { rank: number; previous: number | null }) {
  if (previous === null) return null;
  const delta = previous - rank;
  if (delta === 0) return <span className="font-mono text-[0.65rem] text-chalk-dim">–</span>;
  return (
    <span className={`font-mono text-[0.65rem] ${delta > 0 ? 'text-cash' : 'text-blood'}`}>
      {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}
    </span>
  );
}

function Header({ season }: { season: number }) {
  return (
    <div>
      <p className="eyebrow">{season} · before kickoff</p>
      <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">Tip Sheet</h1>
    </div>
  );
}
